import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Calendar, Users, Music, FileText, Ticket, ExternalLink, ListOrdered } from "lucide-react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { apiClient } from "@/App";
import { isStandalonePwa, handleExternalNav } from "@/lib/utils";
import { gigLocalDate } from "@/utils/dateUtils";

function parseGigDate(dateStr) {
  // Build a local Date object from the user-local YYYY-MM-DD so format() renders correctly.
  const localDateStr = gigLocalDate(dateStr);
  if (!localDateStr) return null;
  const parts = localDateStr.split('-');
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return null;
}

// mode="public": family-safe link (/share/gig/:shareToken)
// mode="dep": full working detail for a deputy (/dep/gig/:depToken)
export default function SharedGigView({ mode = "public" }) {
  const params = useParams();
  const token = mode === "dep" ? params.depToken : params.shareToken;
  const [gig, setGig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchGig = async () => {
      try {
        const res = await apiClient.get(mode === "dep" ? `/share/dep/${token}` : `/share/gig/${token}`);
        setGig(res.data);
        // Set dynamic page title and OG meta tags
        const g = res.data;
        const title = g.venue_name ? `${g.artist_name} @ ${g.venue_name}` : g.artist_name;
        document.title = `${title} — Fretless`;
        // Update OG tags for any in-app sharing
        const setMeta = (prop, content) => {
          let el = document.querySelector(`meta[property="${prop}"]`);
          if (el) el.setAttribute('content', content);
        };
        setMeta('og:title', title);
        const dateStr = g.date ? new Date((gigLocalDate(g.date) || '').replace(/-/g, '/')).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
        const desc = [dateStr, g.start_time ? `at ${g.start_time}` : '', g.city].filter(Boolean).join(' — ');
        setMeta('og:description', desc);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchGig();
    return () => { document.title = 'Fretless'; };
  }, [token, mode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !gig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="shared-gig-not-found">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Music className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-xl font-semibold mb-2">Gig not found</h2>
            <p className="text-muted-foreground text-sm">
              This gig link is invalid or sharing has been disabled.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gigDate = parseGigDate(gig.date);
  const gigEndDate = gig.end_date && gig.end_date.slice(0, 10) !== gig.date?.slice(0, 10)
    ? parseGigDate(gig.end_date) : null;
  const formattedDate = gigDate
    ? gigEndDate
      ? `${format(gigDate, "MMMM d")}–${format(gigEndDate, "MMMM d, yyyy")}`
      : format(gigDate, "EEEE, MMMM d, yyyy")
    : "Date TBA";
  const mapsQuery = encodeURIComponent(
    [gig.venue_name, gig.venue_address, gig.city].filter(Boolean).join(", ")
  );

  // Running order for the day — [{label, time}]. Keep only rows with a label
  // or a time, and sort by time ascending (timeless rows fall to the end).
  const scheduleToMinutes = (t) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec((t || "").trim());
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : Number.POSITIVE_INFINITY;
  };
  const scheduleItems = (gig.schedule || [])
    .filter((it) => it && ((it.label || "").trim() || (it.time || "").trim()))
    .sort((a, b) => scheduleToMinutes(a.time) - scheduleToMinutes(b.time));

  return (
    <div className="min-h-screen bg-background" data-testid="shared-gig-view">
      {/* Top accent bar */}
      <div className="h-1 bg-primary w-full" />

      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
        {/* Header */}
        <div className="space-y-1" data-testid="shared-gig-header">
          <p className="font-mono text-xs uppercase tracking-widest text-primary">Gig Details</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {gig.artist_name}
          </h1>
        </div>

        {/* Date & Time */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium" data-testid="shared-gig-date">{formattedDate}</p>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                {gig.start_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {gig.start_time}
                    {gig.end_time && ` - ${gig.end_time}`}
                  </span>
                )}
                {gig.call_time && (
                  <span className="font-mono text-xs">Call: {gig.call_time}</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Running Order / Set times */}
        {scheduleItems.length > 0 && (
          <Card className="bg-card border-border" data-testid="shared-gig-schedule">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <ListOrdered className="h-5 w-5 text-primary" />
                </div>
                <p className="font-medium">Running Order</p>
              </div>
              <div className="ml-14 space-y-2">
                {scheduleItems.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-baseline gap-3 text-sm"
                    data-testid={`shared-schedule-row-${i}`}
                  >
                    <span className="font-mono text-xs text-primary w-12 shrink-0 tabular-nums">
                      {(item.time || "").trim() || "—"}
                    </span>
                    <span className="min-w-0">{(item.label || "").trim() || "—"}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Venue */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium" data-testid="shared-gig-venue">{gig.venue_name}</p>
                {gig.venue_address && (
                  <p className="text-sm text-muted-foreground mt-0.5">{gig.venue_address}</p>
                )}
                <p className="text-sm text-muted-foreground">{gig.city}{gig.country ? `, ${gig.country}` : ""}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 ml-14 gap-2"
              asChild
              data-testid="shared-gig-maps-btn"
            >
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (isStandalonePwa()) {
                    e.preventDefault();
                    window.location.href = e.currentTarget.href;
                  }
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in Maps
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* People */}
        {gig.performers && gig.performers.length > 0 && (
          <Card className="bg-card border-border" data-testid="shared-gig-people">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <p className="font-medium">Musicians</p>
              </div>
              <div className="ml-14 space-y-1.5">
                {gig.performers.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span>{p.name}</span>
                    {p.instrument && (
                      <span className="text-muted-foreground">({p.instrument})</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dress Code */}
        {gig.dress_code && (
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-lg">👔</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Dress Code</p>
                <p className="text-sm mt-0.5">{gig.dress_code}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fee */}
        {gig.fee != null && gig.fee !== "" && (
          <Card className="bg-card border-border" data-testid="shared-gig-fee">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-lg">💷</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Fee</p>
                <p className="text-sm mt-0.5">
                  {({ GBP: "£", EUR: "€", USD: "$" })[gig.fee_currency] || `${gig.fee_currency} `}{gig.fee}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {gig.gig_notes && (
          <Card className="bg-card border-border" data-testid="shared-gig-notes">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{gig.gig_notes}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tickets */}
        {gig.ticket_link && (
          <Button className="w-full gap-2 bg-primary text-primary-foreground" asChild data-testid="shared-gig-tickets-btn">
            <a href={gig.ticket_link} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav}>
              <Ticket className="h-4 w-4" />
              Get Tickets
              {gig.ticket_price && ` - ${gig.ticket_price}`}
            </a>
          </Button>
        )}

        {/* Sign-up CTA — turn recipients into Fretless users */}
        <Card className="bg-primary/5 border-primary/20 mt-2" data-testid="shared-gig-cta">
          <CardContent className="p-5 text-center space-y-3">
            <p className="text-sm font-medium">
              Want to share gig details like this at the touch of a button — plus setlists, invoices, travel times and much more?
            </p>
            <Button
              className="w-full gap-2 bg-primary text-primary-foreground"
              asChild
              data-testid="shared-gig-cta-btn"
            >
              <a
                href="https://www.fretless.life"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Music className="h-4 w-4" />
                Get Fretless — it's free to start
              </a>
            </Button>
            <p className="text-xs text-muted-foreground">
              www.fretless.life · the musician's command center
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground pt-2">
          Shared via Fretless
        </p>
      </div>
    </div>
  );
}
